import { P } from 'components'
import PMBInputNumber from 'components/PMBInputNumber'
import PMBSelect from 'components/PMBSelect'
import PMBTooltip from 'components/PMBTooltip'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import { HelpCircle, Info } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { Button, FormGroup, Input, Modal, ModalBody, Spinner } from 'reactstrap'
import { formatInputNumber } from 'utility/Utils'
import { getDataCommodities } from './configurationsListAction'
import { useAddConfigurationMutation } from './configurationsListApiSlice'

const ModalAddConfiguration = ({
  isOpen,
  toggle,
  onSuccess,
  dataExistingConfiguration,
  dataConfigurationById,
}) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { commodities: commoditiesList } = useSelector(
    (state) => state.configurationsList,
  )

  const isEditMode = Boolean(dataConfigurationById)

  const [addConfiguration, { isLoading: isAdding }] =
    useAddConfigurationMutation()

  const { data } = useSelector((state) => state.publicVersion)
  const unitOptions = data?.unitArray ?? []

  const inputRef = useRef(null)

  const [payload, setPayload] = useState({
    id: null,
    type: null,
    psdh: null,
    unit: unitOptions?.[0],
    commodities: [],
  })

  const resetState = () => {
    setPayload({
      id: null,
      type: null,
      psdh: null,
      unit: unitOptions?.[0],
      commodities: [],
    })
  }

  // HANDLE CHANGE VALUE
  const handleChange = (e) => {
    const { name, value } = e.target
    setPayload((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async () => {
    try {
      if (isFormInvalid) {
        enqueueSnackbar('Lengkapi data terlebih dahulu', {
          variant: 'error',
        })
        return
      }

      let updatedConfig = dataExistingConfiguration

      if (isEditMode) {
        updatedConfig = updatedConfig.map((category) => {
          if (category.id === payload.originalId) {
            const updatedCommodities = category.commodities.map((commodity) => {
              if (commodity.id === payload.id) {
                return {
                  ...commodity,
                  psdh: payload.psdh,
                  unit: payload.unit,
                  commodity: {
                    ...commodity.commodity,
                    ...payload.commodities,
                  },
                }
              }
              return commodity
            })
            return {
              ...category,
              id: payload.id,
              type: payload.type,
              commodities: updatedCommodities,
            }
          }
          return category
        })
      }

      const response = await addConfiguration(updatedConfig)
      if (response?.data) {
        enqueueSnackbar('Konfigurasi berhasil diperbarui', {
          variant: 'success',
        })
        toggle()
        resetState()
        onSuccess()
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? 'Konfigurasi gagal diperbarui', {
        variant: 'error',
      })
    }
  }

  // HANDLE CHANGE PSDH
  const handleAmountChange = useCallback(
    (e) => {
      const value = e.target.value
      const replaceValue = value.replace(/[^\d,]/g, '')

      const [integerPart, decimalPart] = replaceValue.split(',')

      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

      const formattedValue = decimalPart
        ? `${formattedInteger},${decimalPart}`
        : formattedInteger

      const numericValue =
        parseFloat(formattedValue.replace(/\./g, '').replace(',', '.')) || null

      setPayload((prev) => ({ ...prev, psdh: numericValue }))
    },
    [payload],
  )

  const isFormInvalid =
    !payload?.type || !payload?.psdh || !payload?.unit || !payload?.commodities

  useEffect(() => {
    if (isOpen && isEditMode) {
      setPayload({
        originalId: dataConfigurationById?.originalId,
        id: dataConfigurationById?.id,
        type: dataConfigurationById?.type,
        psdh: dataConfigurationById?.psdh,
        unit: {
          label: dataConfigurationById?.unit,
          value: dataConfigurationById?.unit,
        },
        commodities: dataConfigurationById?.commodity
          ? [
              {
                label: dataConfigurationById.commodity,
                value: dataConfigurationById.commodity,
              },
            ]
          : [],
      })
    }
  }, [isOpen, isEditMode, dataConfigurationById])

  // AUTO FOCUS
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100)
  }, [isOpen])

  // GET COMMODITIES
  useEffect(() => {
    if (isOpen) {
      dispatch(getDataCommodities())
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen, dispatch])

  // RESET STATE PAYLOAD
  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        resetState()
      }, 300)

      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  return (
    <>
      <Modal
        className="p-4"
        centered
        isOpen={isOpen}
        toggle={toggle}
        autoFocus={false}
        style={{ maxWidth: '600px' }}
        data-testid="configurations-modal"
      >
        <ModalBody>
          <div className="d-flex flex-column gap-1">
            <P className="text-sm pb-2" size="medium" color="#101828" bold>
              Ubah Konfigurasi
            </P>

            <div className="d-flex align-items-center gap-2 p-2 bg-gray-100 rounded-2">
              <Info size={16} color="#006386" />
              <P size="small">
                Perubahan jenis akan berlaku untuk semua barang dalam jenis
                yang sama.
              </P>
            </div>

            <div
              className="pt-2 pb-4 overflow-y-auto"
              style={{
                maxHeight: 550,
              }}
            >
              <FormGroup>
                <P className="text-sm pb-1" size="regular" color="#344054">
                  Jenis<span style={{ color: '#b42218' }}>*</span>
                </P>
                <Input
                  placeholder="Masukkan jenis"
                  name="type"
                  value={payload.type || ''}
                  onChange={handleChange}
                  autoComplete="off"
                  innerRef={inputRef}
                  data-testid="configurations-form-type"
                />
              </FormGroup>

              <FormGroup>
                <P className="text-sm pb-1" size="regular" color="#344054">
                  Barang<span style={{ color: '#b42218' }}>*</span>
                </P>
                <PMBSelect
                  value={payload.commodities}
                  options={commoditiesList}
                  onChange={(e) =>
                    setPayload((prev) => ({ ...prev, commodities: e }))
                  }
                  getOptionLabel={(e) => e.label}
                  name="commodities"
                  style={{ width: '100%' }}
                  testId="configurations-form-commodity"
                  // isMulti={true}
                  menuPosition="fixed"
                  menuPlacement="bottom"
                  menuPortalTarget={
                    typeof window !== 'undefined' ? document.body : null
                  }
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#d1d5db',
                      },
                      borderRadius: '8px',
                      padding: '2px',
                      minWidth: '100%',
                      maxWidth: '100%',
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#e0f2ff',
                      borderRadius: '12px',
                      padding: '2px 6px',
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#0369a1',
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#0369a1',
                      '&:hover': {
                        backgroundColor: '#b3e5fc',
                        color: '#005f8e',
                      },
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#6b7280',
                    }),
                    clearIndicator: (provided) => ({
                      ...provided,
                      color: '#6b7280',
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      maxHeight: 120,
                      overflowY: 'auto',
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
              </FormGroup>

              <FormGroup>
                <div className="d-flex align-items-center">
                  <P className="text-sm me-1" size="regular" color="#344054">
                    PSDH
                  </P>
                  <HelpCircle id="tooltip-psdh" width={14} />
                  <span style={{ color: '#b42218' }}>*</span>
                  <PMBTooltip
                    placement="right"
                    target="tooltip-psdh"
                    text="PROVISI SUMBER DAYA HUTAN"
                  />
                </div>
                <PMBInputNumber
                  onFocus={(e) => e.target.select()}
                  value={formatInputNumber(payload.psdh)}
                  onChange={handleAmountChange}
                  name="psdh"
                  placeholder="Masukkan psdh"
                  prefix="Rp"
                  isFirstPrefix
                  testId="configurations-form-psdh"
                />
              </FormGroup>

              <FormGroup>
                <P className="text-sm pb-1" size="regular" color="#344054">
                  Satuan<span style={{ color: '#b42218' }}>*</span>
                </P>
                <PMBSelect
                  value={payload.unit}
                  options={unitOptions}
                  onChange={(e) => setPayload((prev) => ({ ...prev, unit: e }))}
                  name="unit"
                  style={{ width: '100%' }}
                  menuPosition="fixed"
                  testId="configurations-form-unit"
                />
              </FormGroup>
            </div>
          </div>

          <div className="d-flex gap-3 mt-3">
            <Button
              onClick={() => toggle()}
              className="btn-cancel"
              outline
              block
              data-testid="configurations-modal-cancel-button"
            >
              Batal
            </Button>
            <Button
              color="primary"
              block
              disabled={isAdding || isFormInvalid}
              onClick={handleSubmit}
              data-testid="configurations-modal-submit-button"
            >
              {isAdding && <Spinner size="sm" className="me-2" />}
              Simpan
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default ModalAddConfiguration
